import React, { useState } from 'react';
import {
    Calendar,
    CheckSquare,
    Clock,
    Edit,
    Plus,
    Square,
    Trash2,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateNote, useDeleteNote, useNotes, useUpdateNote } from '@/api/notes/use-notes';
import { useUsers } from '@/api/users/use-users';
import { useAuth } from '@/contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';

interface NotesWidgetProps {
    entityType: 'leads' | 'customers';
    entityId: string;
    entityName?: string;
}

export const NotesWidget: React.FC<NotesWidgetProps> = ({ entityType, entityId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { user: currentUser } = useAuth();
    const { hasPermission } = usePermissions();
    const { data: usersData, isLoading: usersLoading } = useUsers({ status: 'active' });

    // Check if user has permission to view notes
    const canViewNotes = hasPermission(ModulePermission.NOTES, PermissionAction.VIEW);
    const canEditNotes = hasPermission(ModulePermission.NOTES, PermissionAction.EDIT);
    const canDeleteNotes = hasPermission(ModulePermission.NOTES, PermissionAction.DELETE);
    const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
    const [quickAddForm, setQuickAddForm] = useState({
        type: 'note' as 'note' | 'task' | 'activity',
        title: '',
        content: '',
        priority: 'medium' as 'low' | 'medium' | 'high',
        assignee_id: currentUser?.id || '',
        due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Today + 1 week
        duration_minutes: '',
        activity_type: 'email' as 'email' | 'call' | 'meeting' | 'other',
        outcome: 'positive' as 'positive' | 'negative' | 'neutral',
        next_action: '',
    });
    const [editingNote, setEditingNote] = useState<any>(null);
    const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(5); // Show 5 notes per page

    // Helper function to format timestamp
    const formatTimestamp = (createdAt: string) => {
        return format(new Date(createdAt), 'MMM d, yyyy h:mm a');
    };

    // Helper function to get full date/time for tooltip
    const getFullDateTime = (createdAt: string) => {
        const date = new Date(createdAt);
        return format(date, "EEEE, MMMM d, yyyy 'at' h:mm:ss a");
    };

    const notesQuery = useNotes(entityType, entityId, {
        page: currentPage,
        per_page: perPage,
    });

    const { data: notesData, isLoading: notesLoading, error: notesError } = notesQuery;

    const createNote = useCreateNote();
    const updateNote = useUpdateNote();
    const deleteNote = useDeleteNote();

    const notes = notesData?.data || [];
    const pagination = notesData?.meta;

    // Check if users list is empty (no permission to list users)
    const hasNoUsers = !usersLoading && (!usersData?.data || usersData.data.length === 0);
    const shouldDisableAssignee = hasNoUsers && quickAddForm.type === 'task';

    // Auto-set current user as assignee when users list is empty and creating a task
    React.useEffect(() => {
        if (
            shouldDisableAssignee &&
            currentUser?.id &&
            quickAddForm.type === 'task' &&
            !quickAddForm.assignee_id
        ) {
            setQuickAddForm(prev => ({
                ...prev,
                assignee_id: currentUser.id,
            }));
        }
    }, [shouldDisableAssignee, currentUser?.id, quickAddForm.type, quickAddForm.assignee_id]);

    const resetQuickAddForm = () => {
        setQuickAddForm({
            type: 'note',
            title: '',
            content: '',
            priority: 'medium',
            assignee_id: currentUser?.id || '',
            due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Today + 1 week
            duration_minutes: '',
            activity_type: 'email',
            outcome: 'positive',
            next_action: '',
        });
        setIsQuickAddOpen(false);
        setCurrentPage(1); // Reset to first page when adding new notes
    };

    const handleQuickAdd = async () => {
        if (!quickAddForm.content.trim()) return;

        // Validate task-specific fields
        if (quickAddForm.type === 'task' && !quickAddForm.due_date) {
            enqueueSnackbar('Due date is required for tasks', { variant: 'error' });
            return;
        }
        if (quickAddForm.type === 'task' && !quickAddForm.assignee_id) {
            enqueueSnackbar('Assignee is required for tasks', { variant: 'error' });
            return;
        }

        try {
            const noteData: any = {
                entityType: entityType,
                entityId: entityId,
                type: quickAddForm.type,
                title: quickAddForm.title.trim(),
                content: quickAddForm.content.trim(),
            };

            // Add type-specific fields
            if (quickAddForm.type === 'task') {
                noteData.priority = quickAddForm.priority;
                if (quickAddForm.assignee_id) {
                    noteData.assignee_id = quickAddForm.assignee_id;
                }
                noteData.due_date = quickAddForm.due_date; // Always include due_date for tasks
            } else if (quickAddForm.type === 'activity') {
                noteData.activity_type = quickAddForm.activity_type;
                noteData.outcome = quickAddForm.outcome;
                if (quickAddForm.duration_minutes) {
                    noteData.duration_minutes = parseInt(quickAddForm.duration_minutes);
                }
                if (quickAddForm.next_action) {
                    noteData.next_action = quickAddForm.next_action.trim();
                }
            }

            await createNote.mutateAsync({
                entityType: entityType,
                entityId: entityId,
                input: noteData,
            });
            resetQuickAddForm();
            enqueueSnackbar('Note added successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to add note', { variant: 'error' });
        }
    };

    const handleEdit = (note: any) => {
        setEditingNote(note);
    };

    const handleSaveEdit = async () => {
        if (!editingNote || !editingNote.content.trim()) return;

        try {
            await updateNote.mutateAsync({
                entityType: entityType,
                entityId: entityId,
                noteId: editingNote.id,
                input: {
                    content: editingNote.content.trim(),
                },
            });
            setEditingNote(null);
            enqueueSnackbar('Note updated successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to update note', { variant: 'error' });
        }
    };

    const handleDelete = async (noteId: string) => {
        if (!window.confirm('Are you sure you want to delete this note?')) return;

        try {
            await deleteNote.mutateAsync({
                entityType: entityType,
                entityId: entityId,
                noteId: noteId,
            });
            enqueueSnackbar('Note deleted successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to delete note', { variant: 'error' });
        }
    };

    const handleSelectNote = (noteId: string) => {
        setSelectedNotes(prev =>
            prev.includes(noteId) ? prev.filter(id => id !== noteId) : [...prev, noteId]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedNotes.length === 0) return;

        if (
            !window.confirm(
                `Are you sure you want to delete ${selectedNotes.length} selected notes?`
            )
        )
            return;

        try {
            await Promise.all(
                selectedNotes.map(noteId =>
                    deleteNote.mutateAsync({
                        entityType: entityType,
                        entityId: entityId,
                        noteId: noteId,
                    })
                )
            );
            setSelectedNotes([]);
            enqueueSnackbar(`${selectedNotes.length} notes deleted successfully`, {
                variant: 'success',
            });
        } catch (error) {
            enqueueSnackbar('Failed to delete notes', { variant: 'error' });
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'task':
                return 'bg-blue-100 text-blue-800 border-blue-300';
            case 'activity':
                return 'bg-green-100 text-green-800 border-green-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'bg-red-100 text-red-800 border-red-300';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'low':
                return 'bg-green-100 text-green-800 border-green-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Edit className="h-5 w-5" />
                        Notes & Tasks
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                                Per page
                            </span>
                            <Select
                                value={perPage.toString()}
                                onValueChange={value => {
                                    setPerPage(parseInt(value));
                                    setCurrentPage(1); // Reset to first page when changing page size
                                }}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="5">5</SelectItem>
                                    <SelectItem value="10">10</SelectItem>
                                    <SelectItem value="20">20</SelectItem>
                                    <SelectItem value="50">50</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => notesQuery.refetch()}
                            className="gap-2"
                        >
                            <Edit className="h-4 w-4" />
                            Refresh
                        </Button>
                        {selectedNotes.length > 0 && canDeleteNotes && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleBulkDelete}
                                className="gap-2"
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete ({selectedNotes.length})
                            </Button>
                        )}
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Quick Add Note */}
                {canViewNotes && (
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            <span className="font-medium">Quick Add</span>
                        </div>

                        {/* Always visible content input */}
                        <Textarea
                            placeholder="Type your note here..."
                            value={quickAddForm.content}
                            onChange={e => {
                                setQuickAddForm(prev => ({ ...prev, content: e.target.value }));
                                if (!isQuickAddOpen && e.target.value.trim()) {
                                    setIsQuickAddOpen(true);
                                }
                            }}
                            className="min-h-[80px] resize-none"
                            autoFocus={isQuickAddOpen}
                        />

                        {/* Progressive form reveal */}
                        {isQuickAddOpen && quickAddForm.content.trim() && (
                            <div className="space-y-4">
                                {/* Type Selection */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label htmlFor="type">Type</Label>
                                        <Select
                                            value={quickAddForm.type}
                                            onValueChange={(value: 'note' | 'task' | 'activity') =>
                                                setQuickAddForm(prev => ({ ...prev, type: value }))
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="note">Note</SelectItem>
                                                <SelectItem value="task">Task</SelectItem>
                                                <SelectItem value="activity">Activity</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Title field */}
                                    <div className="md:col-span-2">
                                        <Label htmlFor="title">Title (Optional)</Label>
                                        <Input
                                            id="title"
                                            placeholder="Enter a title..."
                                            value={quickAddForm.title}
                                            onChange={e =>
                                                setQuickAddForm(prev => ({
                                                    ...prev,
                                                    title: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>

                                {/* Task-specific fields */}
                                {quickAddForm.type === 'task' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <Label htmlFor="priority">Priority</Label>
                                            <Select
                                                value={quickAddForm.priority}
                                                onValueChange={(value: 'low' | 'medium' | 'high') =>
                                                    setQuickAddForm(prev => ({
                                                        ...prev,
                                                        priority: value,
                                                    }))
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="low">Low</SelectItem>
                                                    <SelectItem value="medium">Medium</SelectItem>
                                                    <SelectItem value="high">High</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label htmlFor="assignee_id">
                                                Assignee{quickAddForm.type === 'task' ? ' *' : ''}
                                            </Label>
                                            <Select
                                                value={quickAddForm.assignee_id || 'unassigned'}
                                                onValueChange={value =>
                                                    setQuickAddForm(prev => ({
                                                        ...prev,
                                                        assignee_id:
                                                            value === 'unassigned' ? '' : value,
                                                    }))
                                                }
                                                disabled={shouldDisableAssignee}
                                            >
                                                <SelectTrigger disabled={shouldDisableAssignee}>
                                                    <SelectValue
                                                        placeholder={
                                                            shouldDisableAssignee
                                                                ? currentUser?.name ||
                                                                  'You (no permission to view other users)'
                                                                : 'Select assignee'
                                                        }
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {quickAddForm.type !== 'task' && (
                                                        <SelectItem value="unassigned">
                                                            Unassigned
                                                        </SelectItem>
                                                    )}
                                                    {usersLoading ? (
                                                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                                            Loading users...
                                                        </div>
                                                    ) : usersData?.data &&
                                                      usersData.data.length > 0 ? (
                                                        usersData.data.map(user => (
                                                            <SelectItem
                                                                key={user.id}
                                                                value={user.id}
                                                            >
                                                                {user.name}{' '}
                                                                {user.id === currentUser?.id
                                                                    ? '(Me)'
                                                                    : ''}
                                                            </SelectItem>
                                                        ))
                                                    ) : (
                                                        currentUser && (
                                                            <SelectItem
                                                                value={currentUser.id}
                                                                disabled
                                                            >
                                                                {currentUser.name} (You)
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            {shouldDisableAssignee && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    You don't have permission to view other users.
                                                    Task will be assigned to you.
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <Label htmlFor="due_date">Due Date</Label>
                                            <Input
                                                id="due_date"
                                                type="date"
                                                min={
                                                    new Date(Date.now() + 24 * 60 * 60 * 1000)
                                                        .toISOString()
                                                        .split('T')[0]
                                                } // Tomorrow
                                                value={quickAddForm.due_date}
                                                onChange={e =>
                                                    setQuickAddForm(prev => ({
                                                        ...prev,
                                                        due_date: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Activity-specific fields */}
                                {quickAddForm.type === 'activity' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <Label htmlFor="activity_type">Activity Type</Label>
                                            <Select
                                                value={quickAddForm.activity_type}
                                                onValueChange={(
                                                    value: 'email' | 'call' | 'meeting' | 'other'
                                                ) =>
                                                    setQuickAddForm(prev => ({
                                                        ...prev,
                                                        activity_type: value,
                                                    }))
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="email">Email</SelectItem>
                                                    <SelectItem value="call">Call</SelectItem>
                                                    <SelectItem value="meeting">Meeting</SelectItem>
                                                    <SelectItem value="other">Other</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label htmlFor="outcome">Outcome</Label>
                                            <Select
                                                value={quickAddForm.outcome}
                                                onValueChange={(
                                                    value: 'positive' | 'negative' | 'neutral'
                                                ) =>
                                                    setQuickAddForm(prev => ({
                                                        ...prev,
                                                        outcome: value,
                                                    }))
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="positive">
                                                        Positive
                                                    </SelectItem>
                                                    <SelectItem value="negative">
                                                        Negative
                                                    </SelectItem>
                                                    <SelectItem value="neutral">Neutral</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label htmlFor="duration_minutes">Duration (min)</Label>
                                            <Input
                                                id="duration_minutes"
                                                type="number"
                                                placeholder="30"
                                                value={quickAddForm.duration_minutes}
                                                onChange={e =>
                                                    setQuickAddForm(prev => ({
                                                        ...prev,
                                                        duration_minutes: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Next Action for Activities */}
                                {quickAddForm.type === 'activity' && (
                                    <div>
                                        <Label htmlFor="next_action">Next Action (Optional)</Label>
                                        <Input
                                            id="next_action"
                                            placeholder="What should happen next?"
                                            value={quickAddForm.next_action}
                                            onChange={e =>
                                                setQuickAddForm(prev => ({
                                                    ...prev,
                                                    next_action: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleQuickAdd}
                                        disabled={
                                            !quickAddForm.content.trim() || createNote.isPending
                                        }
                                    >
                                        {createNote.isPending ? 'Adding...' : 'Add Note'}
                                    </Button>
                                    <Button variant="outline" onClick={resetQuickAddForm}>
                                        Reset
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Notes List */}
                {!canViewNotes ? (
                    <div className="text-center py-4 text-muted-foreground">
                        <Edit className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>You don't have permission to view notes</p>
                        <p className="text-sm">Contact your administrator for access</p>
                    </div>
                ) : notesLoading ? (
                    <div className="space-y-3 p-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="space-y-2 p-3 border rounded-md">
                                <div className="flex items-center gap-2">
                                    <Skeleton className="h-4 w-4 rounded-full" />
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="h-4 w-20 ml-auto" />
                                </div>
                                <Skeleton
                                    className="h-4"
                                    style={{ width: `${65 + ((i * 11) % 25)}%` }}
                                />
                                <Skeleton className="h-4 w-1/2" />
                            </div>
                        ))}
                    </div>
                ) : notesError ? (
                    <div className="text-center py-4 text-red-500">
                        <p>Failed to load notes</p>
                        <p className="text-sm text-muted-foreground mt-2">
                            {notesError?.message?.includes('403') ||
                            notesError?.message?.includes('Unauthorized')
                                ? "You don't have permission to view notes. Contact your administrator for access."
                                : `Error: ${notesError?.message || 'Unknown error'}`}
                        </p>
                        {!notesError?.message?.includes('403') &&
                            !notesError?.message?.includes('Unauthorized') && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    Check console for details
                                </p>
                            )}
                    </div>
                ) : notes.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                        <Edit className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No notes yet</p>
                        <p className="text-sm">Start typing above to create your first note</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {notes.map((note: any) => (
                            <div key={note.id} className="border rounded-lg p-3 space-y-2">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-2 flex-1">
                                        {canDeleteNotes && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleSelectNote(note.id)}
                                                className="p-1 h-auto"
                                            >
                                                {selectedNotes.includes(note.id) ? (
                                                    <CheckSquare className="h-4 w-4" />
                                                ) : (
                                                    <Square className="h-4 w-4" />
                                                )}
                                            </Button>
                                        )}
                                        <div className="flex-1 space-y-2">
                                            {/* Type and Priority Badges */}
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant="outline"
                                                    className={getTypeColor(note.type)}
                                                >
                                                    {note.type === 'note'
                                                        ? 'Note'
                                                        : note.type === 'task'
                                                          ? 'Task'
                                                          : 'Activity'}
                                                </Badge>
                                                {note.type === 'task' && note.priority && (
                                                    <Badge
                                                        variant="outline"
                                                        className={getPriorityColor(note.priority)}
                                                    >
                                                        {note.priority}
                                                    </Badge>
                                                )}
                                                {note.type === 'task' && note.assignee && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        Assigned to: {note.assignee.name}
                                                    </Badge>
                                                )}
                                                {note.title && (
                                                    <span className="font-medium text-sm">
                                                        {note.title}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Content */}
                                            {editingNote?.id === note.id ? (
                                                <div className="space-y-2">
                                                    <Textarea
                                                        value={editingNote.content}
                                                        onChange={e =>
                                                            setEditingNote({
                                                                ...editingNote,
                                                                content: e.target.value,
                                                            })
                                                        }
                                                        className="min-h-[60px] resize-none"
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            onClick={handleSaveEdit}
                                                            disabled={updateNote.isPending}
                                                        >
                                                            {updateNote.isPending
                                                                ? 'Saving...'
                                                                : 'Save'}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setEditingNote(null)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                                    {note.content}
                                                </p>
                                            )}

                                            {/* Task-specific fields */}
                                            {note.type === 'task' && (
                                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                    {note.due_date && (
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="h-3 w-3" />
                                                            <DateTimeFormatter
                                                                date={note.due_date}
                                                            />
                                                        </div>
                                                    )}
                                                    {note.duration_minutes && (
                                                        <div className="flex items-center gap-1">
                                                            <Clock className="h-3 w-3" />
                                                            {note.duration_minutes} min
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Metadata */}
                                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                <span>By {note.author?.name || 'Unknown'}</span>
                                                <span
                                                    className="cursor-help"
                                                    title={getFullDateTime(note.created_at)}
                                                >
                                                    {formatTimestamp(note.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {(canEditNotes || canDeleteNotes) && (
                                        <div className="flex items-center gap-1">
                                            {canEditNotes && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleEdit(note)}
                                                    className="p-1 h-auto"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            )}
                                            {canDeleteNotes && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(note.id)}
                                                    className="p-1 h-auto text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Pagination Controls */}
                        {(pagination ? pagination.total > 5 : notes.length > 5) ? (
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t">
                                <div className="text-sm text-muted-foreground">
                                    {pagination ? (
                                        <>
                                            Showing{' '}
                                            {(pagination.current_page - 1) * pagination.per_page +
                                                1}{' '}
                                            to{' '}
                                            {Math.min(
                                                pagination.current_page * pagination.per_page,
                                                pagination.total
                                            )}{' '}
                                            of {pagination.total} notes
                                        </>
                                    ) : (
                                        `Showing ${notes.length} notes`
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                                            Per page
                                        </span>
                                        <Select
                                            value={perPage.toString()}
                                            onValueChange={value => {
                                                setPerPage(parseInt(value));
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <SelectTrigger className="w-[70px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="5">5</SelectItem>
                                                <SelectItem value="10">10</SelectItem>
                                                <SelectItem value="20">20</SelectItem>
                                                <SelectItem value="50">50</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            setCurrentPage(prev => Math.max(1, prev - 1))
                                        }
                                        disabled={
                                            pagination
                                                ? pagination.current_page <= 1
                                                : currentPage <= 1
                                        }
                                        className="gap-1"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        Previous
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                        {pagination
                                            ? `Page ${pagination.current_page} of ${pagination.last_page}`
                                            : `Page ${currentPage}`}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        disabled={
                                            pagination
                                                ? pagination.current_page >= pagination.last_page
                                                : notes.length < perPage
                                        }
                                        className="gap-1"
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
